/**
 * Pure analytics computation functions.
 * No React hooks — these are safe to call inside useMemo or use directly.
 */

import type { RawMessage } from "../components/types"

// SLA hours by level (mirrors HITL useConversations)
export const SLA_HOURS_MAP: Record<string, number> = {
  "5": 1, "critical": 1,
  "4": 4, "high": 4,
  "3": 72, "moderate": 72,
  "2": 168, "mild": 168,
  "1": 168, "low": 168,
}

export function getSlaHoursForLevel(level: string): number | null {
  const norm = level.toLowerCase().trim()
  if (SLA_HOURS_MAP[norm]) return SLA_HOURS_MAP[norm]
  const num = norm.match(/\d+/)
  if (num && SLA_HOURS_MAP[num[0]]) return SLA_HOURS_MAP[num[0]]
  for (const [k, h] of Object.entries(SLA_HOURS_MAP)) {
    if (norm.includes(k)) return h
  }
  return null
}

export function parseLevelNum(level: string): number {
  const norm = level.toLowerCase().trim()
  const numMatch = norm.match(/\d+/)
  if (numMatch) return parseInt(numMatch[0])
  if (norm.includes("critical")) return 5
  if (norm.includes("high")) return 4
  if (norm.includes("moderate")) return 3
  if (norm.includes("mild")) return 2
  if (norm.includes("low")) return 1
  return 0
}

/** Compute response latencies and rate from messages grouped by contact. */
export function computeResponseMetrics(messagesByContact: Map<string, RawMessage[]>): {
  latencies: number[]
  incomingWithResponse: number
  totalIncoming: number
} {
  let incomingWithResponse = 0
  let totalIncoming = 0
  const latencies: number[] = []

  messagesByContact.forEach(msgs => {
    const sorted = [...msgs].sort((a, b) => a.time_created - b.time_created)
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].direction === "incoming") {
        totalIncoming++
        let hasReply = false
        for (let j = i + 1; j < sorted.length && !hasReply; j++) {
          if (sorted[j].direction === "outgoing") hasReply = true
        }
        if (hasReply) incomingWithResponse++
      }
      if (i > 0 && sorted[i].direction === "outgoing" && sorted[i - 1].direction === "incoming") {
        const latency = sorted[i].time_created - sorted[i - 1].time_created
        if (latency > 0 && latency < 3600) latencies.push(latency)
      }
    }
  })

  return { latencies, incomingWithResponse, totalIncoming }
}

/**
 * True when an outgoing message was written by a person rather than the agent:
 * it either carries a `sender` (operator contact) or came through the HITL
 * flow (role === "human"). Mirrors the predicate used in useAnalytics.
 */
export function isHumanResponse(m: RawMessage): boolean {
  return m.sender != null || m.role === "human"
}

/**
 * Business-hours window used to measure moderator responsiveness.
 * `days` holds JS day numbers (0 = Sunday … 6 = Saturday) that are working days.
 * `startHour`/`endHour` are wall-clock hours (0-24) in `timeZone`, so 9-17 is
 * 09:00 up to 17:00. All interpreted in `timeZone`, an IANA name.
 */
export type ResponseAggregation = "median" | "average"

/**
 * Which messages a response metric counts, relative to business hours.
 * - "all": every message, business hours ignored for filtering
 * - "inside": only messages that arrived during business hours
 * - "outside": only messages that arrived outside them
 */
export type BusinessHoursScope = "all" | "inside" | "outside"

export interface BusinessHoursConfig {
  enabled: boolean
  days: number[]
  startHour: number
  endHour: number
  timeZone: string
  /** How the per-reply times are collapsed into the headline number. */
  aggregation: ResponseAggregation
  /**
   * Which arrivals to count. Independent of `enabled`: this picks *which*
   * messages qualify, while `enabled` decides whether the waiting clock pauses
   * outside the window.
   */
  scope: BusinessHoursScope
}

export const DEFAULT_BUSINESS_HOURS: BusinessHoursConfig = {
  enabled: false,
  days: [1, 2, 3, 4, 5],
  startHour: 9,
  endHour: 17,
  timeZone: typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    : "UTC",
  aggregation: "median",
  scope: "all",
}

/**
 * Wall-clock day-of-week and fractional hour for an instant, in `timeZone`.
 * Uses Intl rather than a tz library so this stays dependency-free and DST is
 * handled by the platform's own tz database.
 */
function zonedParts(epochSeconds: number, timeZone: string): { day: number; hour: number } {
  const d = new Date(epochSeconds * 1000)
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  }).formatToParts(d)

  const get = (t: string) => parts.find(p => p.type === t)?.value ?? "0"
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  // hour12:false can yield "24" at midnight in some engines; normalise to 0.
  const rawHour = parseInt(get("hour"), 10) % 24
  const hour = rawHour + parseInt(get("minute"), 10) / 60 + parseInt(get("second"), 10) / 3600

  return { day: dayMap[get("weekday")] ?? 0, hour }
}

/**
 * True when a message arriving at `epochSeconds` should be counted, given the
 * configured scope. Scope "all" accepts everything.
 */
export function matchesScope(epochSeconds: number, cfg: BusinessHoursConfig): boolean {
  if (cfg.scope === "all") return true
  const inside = isWithinBusinessHours(epochSeconds, cfg)
  return cfg.scope === "inside" ? inside : !inside
}

/** True when an instant falls inside the configured business hours. */
export function isWithinBusinessHours(epochSeconds: number, cfg: BusinessHoursConfig): boolean {
  const { day, hour } = zonedParts(epochSeconds, cfg.timeZone)
  if (!cfg.days.includes(day)) return false
  return hour >= cfg.startHour && hour < cfg.endHour
}

/**
 * Seconds of business time between two instants — the clock runs only inside
 * the configured window and pauses outside it, so a Friday-evening message
 * answered Monday morning counts as minutes rather than days.
 *
 * Walks the span in 15-minute steps and sums the ones that land in-hours.
 * The granularity keeps this cheap over long spans while staying well below
 * the resolution anyone reads off a median; it also means DST shifts and
 * timezone changes are absorbed automatically, since every step is re-evaluated
 * in the target zone.
 */
export function businessSecondsBetween(
  startSec: number,
  endSec: number,
  cfg: BusinessHoursConfig
): number {
  if (endSec <= startSec) return 0

  const STEP = 15 * 60
  let total = 0

  for (let t = startSec; t < endSec; t += STEP) {
    const span = Math.min(STEP, endSec - t)
    // Sample the midpoint so a step straddling an edge is attributed to
    // whichever side holds most of it.
    if (isWithinBusinessHours(t + span / 2, cfg)) total += span
  }

  return Math.round(total)
}

/**
 * Moderator response times, in seconds.
 *
 * For each moderator reply, measures from the *last* end-user message that
 * preceded it. When a moderator sends several messages back to back, only the
 * first is counted — the follow-ups are part of the same response, not new
 * ones, so timing them would understate the wait.
 *
 * Bot messages in between are ignored: they neither reset the clock nor count
 * as a response, since the user is still waiting on a human. A moderator reply
 * with no preceding user message (an unprompted outreach) is skipped, as there
 * is no wait to measure.
 *
 * When `businessHours` is enabled the clock runs only inside the configured
 * window, so a Friday-evening message answered Monday morning counts as the
 * minutes actually within working hours rather than the whole weekend.
 *
 * Unlike `computeResponseMetrics`, no upper bound is applied. Moderator replies
 * legitimately take hours or days, and discarding those would bias the result
 * toward only the fastest cases.
 */
export function computeModeratorResponseTimes(
  messagesByContact: Map<string, RawMessage[]>,
  businessHours?: BusinessHoursConfig
): number[] {
  const bh = businessHours?.enabled ? businessHours : null
  const times: number[] = []

  messagesByContact.forEach(msgs => {
    const sorted = [...msgs].sort((a, b) => a.time_created - b.time_created)
    let lastIncomingAt: number | null = null
    let prevWasModerator = false

    for (const m of sorted) {
      if (m.direction === "incoming") {
        lastIncomingAt = m.time_created
        prevWasModerator = false
        continue
      }

      if (!isHumanResponse(m)) continue

      // Only the first message in a moderator's run is timed.
      const inScope = lastIncomingAt != null && (!businessHours || matchesScope(lastIncomingAt, businessHours))
      if (!prevWasModerator && lastIncomingAt != null && inScope) {
        const delta = bh
          ? businessSecondsBetween(lastIncomingAt, m.time_created, bh)
          : m.time_created - lastIncomingAt
        if (delta >= 0) times.push(delta)
      }
      prevWasModerator = true
    }
  })

  return times
}

/**
 * Effective response times, in seconds — how long each user message waited for
 * the answer that actually counts.
 *
 * For every incoming user message, looks ahead to the replies before the user's
 * next message. If a human replied in that window, the wait is measured to the
 * *human's first* reply, even when a bot answered sooner: an auto-acknowledgement
 * is not the response the user was waiting for. If only the bot replied, the
 * wait is measured to the bot's first reply.
 *
 * User messages with no reply at all are skipped rather than counted as zero or
 * infinite, since there is no response time to measure.
 *
 * Consecutive user messages collapse: the clock runs from the most recent one
 * before the reply, matching `computeModeratorResponseTimes`.
 *
 * Returns the times split by responder so callers can show the blend or either
 * side alone.
 */
export function computeEffectiveResponseTimes(
  messagesByContact: Map<string, RawMessage[]>,
  businessHours?: BusinessHoursConfig
): { all: number[]; human: number[]; bot: number[] } {
  const bh = businessHours?.enabled ? businessHours : null
  const all: number[] = []
  const human: number[] = []
  const bot: number[] = []

  const elapsed = (from: number, to: number) =>
    bh ? businessSecondsBetween(from, to, bh) : to - from

  messagesByContact.forEach(msgs => {
    const sorted = [...msgs].sort((a, b) => a.time_created - b.time_created)

    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].direction !== "incoming") continue

      const askedAt = sorted[i].time_created
      if (businessHours && !matchesScope(askedAt, businessHours)) continue
      let firstBotAt: number | null = null
      let firstHumanAt: number | null = null

      // Scan replies up to the user's next message.
      for (let j = i + 1; j < sorted.length; j++) {
        const m = sorted[j]
        if (m.direction === "incoming") break
        if (isHumanResponse(m)) {
          if (firstHumanAt == null) firstHumanAt = m.time_created
          break
        }
        if (firstBotAt == null) firstBotAt = m.time_created
      }

      // A human reply wins even when the bot answered first.
      const repliedAt = firstHumanAt ?? firstBotAt
      if (repliedAt == null) continue

      const delta = elapsed(askedAt, repliedAt)
      if (delta < 0) continue

      all.push(delta)
      if (firstHumanAt != null) human.push(delta)
      else bot.push(delta)
    }
  })

  return { all, human, bot }
}

export function computeMean(values: number[]): number {
  if (values.length === 0) return 0
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/**
 * Collapse response times to a single number.
 *
 * Median resists the long tail a few forgotten conversations produce; average
 * counts every wait equally, so it moves when those outliers are the point.
 */
export function aggregateResponseTimes(
  values: number[],
  how: ResponseAggregation
): number {
  return how === "average" ? computeMean(values) : computeMedian(values)
}

export function computeMedian(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}
